import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import api from "@/lib/api";
import { formatMoney } from "@/lib/currency";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Download, Share2, ArrowLeft, ArrowRight, Sparkles, TrendingUp, TrendingDown, Trophy, Target as TargetIcon, PiggyBank, Flame, Medal, Crown } from "lucide-react";

const BADGE_ICONS = { flame: Flame, medal: Medal, crown: Crown, trophy: Trophy };

export default function Wrapup() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const cardRef = useRef(null);

  const load = useCallback(async (y, m) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/insights/wrapup?year=${y}&month=${m}`);
      setData(data);
    } catch {
      toast.error(t("toasts.wrapup_load_failed"));
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(year, month); }, [year, month, load]);

  const prevMonth = () => {
    const y = month === 1 ? year - 1 : year;
    const m = month === 1 ? 12 : month - 1;
    setYear(y); setMonth(m);
  };
  const nextMonth = () => {
    const y = month === 12 ? year + 1 : year;
    const m = month === 12 ? 1 : month + 1;
    if (new Date(y, m - 1, 1) > new Date()) return;
    setYear(y); setMonth(m);
  };
  const isCurrentOrFuture = (year === now.getFullYear() && month >= now.getMonth() + 1);

  const generatePng = async () => {
    if (!cardRef.current) return null;
    setExporting(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true, pixelRatio: 2,
        backgroundColor: "#FDFCFB",
        skipFonts: true,
      });
      return dataUrl;
    } catch (e) {
      console.error(e);
      toast.error(t("toasts.export_failed")); return null;
    } finally { setExporting(false); }
  };

  const download = async () => {
    const url = await generatePng();
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `finance-wrapup-${data.year}-${String(data.month).padStart(2,"0")}.png`;
    a.click();
    toast.success(t("toasts.wrapup_saved"));
  };

  const share = async () => {
    const url = await generatePng();
    if (!url) return;
    try {
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], `wrapup-${data.year}-${data.month}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: `${data.month_name} ${data.year} Financial Wrap-up`,
          text: `${data.ai_headline} · Health score ${data.health_score}/100`,
          files: [file],
        });
      } else {
        await navigator.clipboard.writeText(`${data.ai_headline} — ${data.month_name} ${data.year} · Health score ${data.health_score}/100`);
        toast.success(t("toasts.highlight_copied"));
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(t("toasts.share_failed"));
    }
  };

  if (loading || !data) {
    return <div className="text-sm text-[var(--text-muted)]">Preparing your wrap-up…</div>;
  }
  const cur = data.currency || user?.currency || "USD";
  const scoreDelta = data.health_score - data.prev_health_score;
  const scoreTone = scoreDelta > 0 ? "up" : scoreDelta < 0 ? "down" : "flat";

  return (
    <div>
      <PageHeader
        subtitle={t("pages.wrapup.subtitle")}
        title={`${data.month_name} ${data.year}`}
        action={
          <div className="flex items-center gap-2">
            <button className="btn-ghost" onClick={download} disabled={exporting} data-testid="wrapup-download-btn">
              <Download size={16}/> {exporting ? "Rendering…" : "Download"}
            </button>
            <button className="btn-primary" onClick={share} disabled={exporting} data-testid="wrapup-share-btn">
              <Share2 size={16}/> Share
            </button>
          </div>
        }
      />

      {/* Month navigator */}
      <div className="flex items-center justify-between mb-6 gap-2">
        <button className="btn-ghost" onClick={prevMonth} data-testid="wrapup-prev-month"><ArrowLeft size={15}/> Previous</button>
        <div className="text-sm text-[var(--text-secondary)]">
          Viewing <span className="font-medium text-[var(--text-primary)]">{data.month_name} {data.year}</span>
        </div>
        <button className="btn-ghost" onClick={nextMonth} disabled={isCurrentOrFuture} data-testid="wrapup-next-month">Next <ArrowRight size={15}/></button>
      </div>

      {/* The wrap-up card (this is what gets exported) */}
      <div className="flex justify-center">
        <div
          ref={cardRef}
          data-testid="wrapup-card"
          className="relative overflow-hidden"
          style={{
            width: 720,
            maxWidth: "100%",
            background: "linear-gradient(155deg, #21422F 0%, #2D5A40 45%, #437A55 100%)",
            color: "#FDFCFB",
            borderRadius: 24,
            padding: 44,
            boxShadow: "0 30px 60px -20px rgba(26,46,34,0.45)",
            fontFamily: "var(--font-body)",
          }}
        >
          {/* Grain texture */}
          <div className="absolute inset-0 pointer-events-none opacity-30"
               style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.09) 1px, transparent 1px)", backgroundSize: "3px 3px" }} />
          {/* Subtle blob */}
          <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full"
               style={{ background: "radial-gradient(circle, rgba(224,158,80,0.35) 0%, rgba(224,158,80,0) 70%)" }} />

          <div className="relative">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <div style={{ fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase", opacity: 0.7 }}>
                  Financial Wrap-up
                </div>
                <div style={{ fontFamily: "var(--font-heading)", fontSize: 34, fontWeight: 600, letterSpacing: "-0.02em", marginTop: 6, lineHeight: 1 }}>
                  {data.month_name} <span style={{ opacity: 0.75 }}>{data.year}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {data.badge && (() => {
                  const BadgeIcon = BADGE_ICONS[data.badge.icon] || Flame;
                  return (
                    <div style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "6px 12px", borderRadius: 999,
                      background: "rgba(224,158,80,0.18)",
                      border: "1px solid rgba(224,158,80,0.5)",
                    }} data-testid="wrapup-badge">
                      <BadgeIcon size={14} color="#E09E50"/>
                      <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 600 }}>
                        {data.badge.name} · {data.streak} mo
                      </div>
                    </div>
                  );
                })()}
                <div className="flex items-center gap-2" style={{ opacity: 0.9 }}>
                  <PiggyBank size={20} />
                  <span style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase" }}>PFO</span>
                </div>
              </div>
            </div>

            {/* AI headline */}
            <div style={{
              marginTop: 26, padding: "14px 18px",
              background: "rgba(255,255,255,0.09)",
              border: "1px solid rgba(255,255,255,0.14)",
              borderRadius: 14,
              display: "flex", alignItems: "center", gap: 12,
            }}>
              <Sparkles size={18} style={{ color: "#E09E50", flexShrink: 0 }}/>
              <div style={{ fontSize: 17, fontWeight: 500, fontFamily: "var(--font-heading)", letterSpacing: "-0.01em" }}>
                {data.ai_headline}
              </div>
            </div>

            {/* Hero score */}
            <div style={{ marginTop: 28, display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 20 }}>
              <div style={{ background: "rgba(0,0,0,0.15)", borderRadius: 18, padding: 22 }}>
                <div style={{ fontSize: 10, letterSpacing: "0.24em", opacity: 0.7, textTransform: "uppercase" }}>Health Score</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 6 }}>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 64, fontWeight: 500, letterSpacing: "-0.03em", lineHeight: 1 }} data-testid="wrapup-score">{data.health_score}</div>
                  <div style={{ opacity: 0.6, fontSize: 20, fontFamily: "var(--font-mono)" }}>/100</div>
                </div>
                <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                  {scoreTone === "up" ? <TrendingUp size={14} color="#B5D8B7"/> : scoreTone === "down" ? <TrendingDown size={14} color="#F4B597"/> : null}
                  <span style={{ opacity: 0.85 }}>
                    {scoreDelta > 0 ? `Up ${scoreDelta} from last month` :
                     scoreDelta < 0 ? `Down ${Math.abs(scoreDelta)} from last month` :
                     "Steady vs last month"}
                  </span>
                </div>
              </div>

              <div style={{ background: "rgba(0,0,0,0.15)", borderRadius: 18, padding: 22 }}>
                <div style={{ fontSize: 10, letterSpacing: "0.24em", opacity: 0.7, textTransform: "uppercase" }}>Net Worth</div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 30, fontWeight: 500, letterSpacing: "-0.02em", marginTop: 8, lineHeight: 1.1 }}>
                  {formatMoney(data.net_worth, cur)}
                </div>
                <div style={{ marginTop: 12, fontSize: 12, opacity: 0.75 }}>
                  Savings rate <span style={{ fontFamily: "var(--font-mono)", fontWeight: 500 }}>{data.savings_rate}%</span>
                </div>
              </div>
            </div>

            {/* KPIs */}
            <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
              <MiniKpi label="Income" value={formatMoney(data.monthly_income, cur)} />
              <MiniKpi label="Spent"  value={formatMoney(data.monthly_expense, cur)}
                       delta={data.expense_change_pct !== 0 ? `${data.expense_change_pct > 0 ? "+" : ""}${data.expense_change_pct}% vs prev` : null}
                       deltaTone={data.expense_change_pct < 0 ? "good" : data.expense_change_pct > 0 ? "bad" : "flat"} />
              <MiniKpi label="Net Cashflow" value={formatMoney(data.net_cashflow, cur)} />
            </div>

            {/* Wins & top category / goals */}
            <div style={{ marginTop: 22, display: "grid", gridTemplateColumns: "1.15fr 1fr", gap: 16 }}>
              <div style={{ background: "rgba(255,255,255,0.09)", borderRadius: 16, padding: 18 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <Trophy size={15} color="#E09E50"/>
                  <div style={{ fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", opacity: 0.85 }}>Wins this month</div>
                </div>
                <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 9 }}>
                  {data.wins.map((w, i) => (
                    <li key={`win-${i}-${String(w).slice(0, 20)}`} style={{ fontSize: 13.5, lineHeight: 1.45, display: "flex", gap: 10 }}>
                      <span style={{ color: "#E09E50", fontFamily: "var(--font-mono)", fontWeight: 600, opacity: 0.9 }}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div style={{ background: "rgba(255,255,255,0.09)", borderRadius: 16, padding: 18 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <TargetIcon size={15} color="#E09E50"/>
                  <div style={{ fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", opacity: 0.85 }}>Goal progress</div>
                </div>
                {data.goal_progress.length === 0 ? (
                  <div style={{ fontSize: 13, opacity: 0.75 }}>Set your first goal to see progress here.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {data.goal_progress.map((g) => (
                      <div key={g.id || g.name}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 5 }}>
                          <span style={{ fontWeight: 500 }}>{g.name}</span>
                          <span style={{ fontFamily: "var(--font-mono)", opacity: 0.85 }}>{g.pct}%</span>
                        </div>
                        <div style={{ height: 5, borderRadius: 999, background: "rgba(0,0,0,0.25)", overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${g.pct}%`, background: "linear-gradient(90deg, #E09E50, #F4B597)" }}/>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Bottom footer */}
            <div style={{ marginTop: 22, display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11.5, opacity: 0.8 }}>
              <div>
                {data.top_category && (
                  <>Top category · <span style={{ fontWeight: 600 }}>{data.top_category.name}</span>{" "}
                  <span style={{ fontFamily: "var(--font-mono)", opacity: 0.85 }}>{formatMoney(data.top_category.amount, cur)}</span></>
                )}
              </div>
              <div style={{ letterSpacing: "0.18em", textTransform: "uppercase" }}>
                {data.transactions_logged} txns · {data.user_name}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 text-center">
        <button className="btn-ghost" onClick={() => navigate("/dashboard")} data-testid="wrapup-back-btn">Back to Dashboard</button>
      </div>
    </div>
  );
}

function MiniKpi({ label, value, delta, deltaTone }) {
  const tones = { good: "#B5D8B7", bad: "#F4B597", flat: "rgba(255,255,255,0.7)" };
  return (
    <div style={{ background: "rgba(0,0,0,0.12)", borderRadius: 14, padding: "14px 16px" }}>
      <div style={{ fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", opacity: 0.7 }}>{label}</div>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 20, fontWeight: 500, marginTop: 6, letterSpacing: "-0.01em" }}>{value}</div>
      {delta && (
        <div style={{ marginTop: 4, fontSize: 11, color: tones[deltaTone] || tones.flat }}>{delta}</div>
      )}
    </div>
  );
}
